import {
  Timestamp,
  arrayRemove,
  collection,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { getDownloadURL, listAll, ref, uploadBytes } from 'firebase/storage';
import { db } from '../firebase/firebase';
import storage, { deleteFile } from '../firebase/storage';
import { BaseCRUDManager } from './base/BaseCRUDManager';
import type {
  CreateTeamUpdateGroupInput,
  CreateTeamUpdatePostInput,
  CreateTeamUpdateReplyInput,
  ITeamUpdateGroup,
  ITeamUpdatePollVote,
  ITeamUpdatePost,
  ITeamUpdateReaction,
  ITeamUpdateReply,
  ITeamUpdateRequestResponse,
  TeamUpdateAttachment,
  TeamUpdateImageAttachment,
  TeamUpdateReactionId,
  TeamUpdateRequestAssignee,
  TeamUpdateVoiceAttachment,
  UpdateTeamUpdatePostInput,
  UpdateTeamUpdateReplyInput,
} from '../types/TeamUpdate';
import {
  TEAM_UPDATE_MAX_IMAGES,
  TEAM_UPDATE_MAX_POLL_OPTIONS,
  TEAM_UPDATE_MEMBER_ROLES,
  TEAM_UPDATE_MIN_POLL_OPTIONS,
  TEAM_UPDATE_REACTION_IDS,
} from '../types/TeamUpdate';
import { WorkStreamTodoManager } from './WorkStreamTodoManager';
import { WorkStreamManager } from './WorkStreamManager';
import {
  getTeamUpdateRequestAssignees,
  getTeamUpdateRequestStatusForUser,
} from '../utilities/teamUpdateRequest';
import type { IMember, MemberRole } from '../types/organisation';
import { hasAnyRole } from '../utilities/roleUtils';
import { getOrganisationMembers } from './organisation/members';
import { UserManager } from './UserManager';
import { compressImageForUpload, sanitizeStorageFileName } from '../utils/compressImageForUpload';
import { ensureCrossPlatformVoiceFile } from '../utilities/audioCombiner';
import { getCloudFunction } from '../utilities/cloudFunctions';
import {
  buildAttachmentsFromFlatFields,
  flatMediaFieldsFromAttachments,
  normaliseTeamUpdatePostMedia,
  normaliseTeamUpdateReplyMedia,
} from '../utilities/teamUpdateMedia';
import { totalUnreadFromPostsAndReplies } from '../utilities/teamUpdateUnread';
import {
  getTeamUpdateMemberActivity,
  notifyTeamUpdateMembersAdded,
  type TeamUpdateMemberActivity,
} from '../services/teamUpdateEmailService';

export type TeamUpdateGroupMember = {
  userId: string;
  name: string;
  email?: string;
};

const GROUPS = 'teamUpdateGroups';
const POSTS = 'teamUpdatePosts';
const REPLIES = 'teamUpdateReplies';
const POLL_VOTES = 'teamUpdatePollVotes';
const REQUEST_RESPONSES = 'teamUpdateRequestResponses';
const REACTIONS = 'teamUpdateReactions';
const MEMBER_ACTIVITY = 'teamUpdateMemberActivity';
const CALLS = 'teamUpdateCalls';
const SIGNALLING = 'signaling';

function normaliseRequestAssignees(
  assignees: TeamUpdateRequestAssignee[] | undefined
): TeamUpdateRequestAssignee[] {
  if (!assignees?.length) return [];
  const seen = new Set<string>();
  const next: TeamUpdateRequestAssignee[] = [];
  for (const assignee of assignees) {
    const userId = assignee.userId?.trim();
    if (!userId || seen.has(userId)) continue;
    seen.add(userId);
    next.push({
      userId,
      name: (assignee.name || 'Member').trim() || 'Member',
    });
  }
  return next;
}

function normalisePollOptions(
  options: Array<{ id?: string; label: string }> | undefined
): Array<{ id: string; label: string }> {
  if (!options?.length) return [];
  const seen = new Set<string>();
  const normalised: Array<{ id: string; label: string }> = [];
  options.forEach((option, index) => {
    const label = option.label.trim();
    if (!label) return;
    const baseId =
      (option.id || label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `option-${index + 1}`).slice(
        0,
        40
      );
    let id = baseId;
    let n = 2;
    while (seen.has(id)) {
      id = `${baseId}-${n}`;
      n += 1;
    }
    seen.add(id);
    normalised.push({ id, label });
  });
  return normalised;
}

function mapDocs<T extends { id: string }>(
  snapshot: { forEach: (cb: (docSnap: { id: string; data: () => Record<string, unknown> }) => void) => void }
): T[] {
  const rows: T[] = [];
  snapshot.forEach((docSnap) => {
    rows.push({ id: docSnap.id, ...docSnap.data() } as T);
  });
  return rows;
}

function storagePrefix(groupId: string, messageId: string): string {
  return `team-updates/${groupId}/${messageId}`;
}

async function deleteStorageFolder(path: string): Promise<void> {
  try {
    const folderRef = ref(storage, path);
    const listed = await listAll(folderRef);
    await Promise.all(listed.items.map((item) => deleteFile(item.fullPath).catch(() => undefined)));
    await Promise.all(listed.prefixes.map((prefix) => deleteStorageFolder(prefix.fullPath)));
  } catch {
    // Folder may not exist
  }
}

async function deleteStoragePrefix(groupId: string, messageId: string): Promise<void> {
  await deleteStorageFolder(storagePrefix(groupId, messageId));
}

async function deleteAttachmentFiles(attachments: TeamUpdateAttachment[] | undefined): Promise<void> {
  if (!attachments?.length) return;
  await Promise.all(
    attachments.map((attachment) => deleteFile(attachment.storagePath).catch(() => undefined))
  );
}

function requestVoiceTranscription(input: {
  kind: 'post' | 'reply';
  messageId: string;
  storagePath: string;
}): void {
  void getCloudFunction('transcribeTeamUpdateVoice')(input).catch((error) => {
    console.error('Team update voice transcription failed to start', error);
  });
}

export class TeamUpdateManager {
  static allocateMessageId(kind: 'post' | 'reply' = 'post'): string {
    return doc(collection(db, kind === 'post' ? POSTS : REPLIES)).id;
  }

  static async uploadImageAttachment(
    groupId: string,
    messageId: string,
    file: File
  ): Promise<TeamUpdateImageAttachment> {
    const prepared = await compressImageForUpload(file);
    const safeName = sanitizeStorageFileName(prepared.name || 'image.jpg');
    const storagePath = `${storagePrefix(groupId, messageId)}/${Date.now()}_${safeName}`;
    const fileRef = ref(storage, storagePath);
    await uploadBytes(fileRef, prepared, { contentType: prepared.type || 'image/jpeg' });
    const downloadUrl = await getDownloadURL(fileRef);
    return { type: 'image', storagePath, downloadUrl };
  }

  static async uploadVoiceAttachment(
    groupId: string,
    messageId: string,
    file: File,
    durationMs: number
  ): Promise<TeamUpdateVoiceAttachment> {
    const prepared = await ensureCrossPlatformVoiceFile(file);
    const type = (prepared.type || '').toLowerCase();
    const ext = type.includes('mpeg') || type.includes('mp3')
      ? 'mp3'
      : type.includes('ogg')
        ? 'ogg'
        : type.includes('mp4') || type.includes('m4a') || type.includes('aac')
          ? 'm4a'
          : 'webm';
    const contentType =
      ext === 'mp3'
        ? 'audio/mpeg'
        : ext === 'm4a'
          ? 'audio/mp4'
          : prepared.type || 'audio/webm';
    const storagePath = `${storagePrefix(groupId, messageId)}/${Date.now()}_voice.${ext}`;
    const fileRef = ref(storage, storagePath);
    await uploadBytes(fileRef, prepared, { contentType });
    const downloadUrl = await getDownloadURL(fileRef);
    return {
      type: 'voice',
      storagePath,
      downloadUrl,
      durationMs: Math.max(0, Math.round(durationMs)),
      transcriptStatus: 'pending',
    };
  }

  static async uploadAttachmentsFromDrafts(input: {
    groupId: string;
    messageId: string;
    imageFiles?: File[];
    voice?: { file: File; durationMs: number };
  }): Promise<TeamUpdateAttachment[]> {
    const attachments: TeamUpdateAttachment[] = [];
    const images = (input.imageFiles || []).slice(0, TEAM_UPDATE_MAX_IMAGES);
    for (const file of images) {
      attachments.push(await this.uploadImageAttachment(input.groupId, input.messageId, file));
    }
    if (input.voice?.file) {
      attachments.push(
        await this.uploadVoiceAttachment(
          input.groupId,
          input.messageId,
          input.voice.file,
          input.voice.durationMs
        )
      );
    }
    return attachments;
  }

  static async createGroup(input: CreateTeamUpdateGroupInput): Promise<string> {
    const memberIds = Array.from(new Set(input.memberIds.filter(Boolean)));
    const id = await BaseCRUDManager.create(GROUPS, {
      name: input.name.trim(),
      description: input.description?.trim() || '',
      atAGlance: input.atAGlance?.trim() || '',
      organisationId: input.organisationId,
      memberIds,
      memberRolesAllowed: input.memberRolesAllowed ?? TEAM_UPDATE_MEMBER_ROLES,
      createdBy: input.createdBy,
    });

    void notifyTeamUpdateMembersAdded({
      group: {
        id,
        name: input.name.trim(),
        description: input.description?.trim() || '',
        atAGlance: input.atAGlance?.trim() || '',
        organisationId: input.organisationId,
      },
      memberIds,
      excludeUserId: input.createdBy,
    }).catch((error) => console.error('Team update added emails failed', error));

    return id;
  }

  static async updateGroup(
    groupId: string,
    updates: Partial<
      Pick<ITeamUpdateGroup, 'name' | 'description' | 'atAGlance' | 'memberIds' | 'memberRolesAllowed'>
    >
  ): Promise<void> {
    const previous =
      updates.memberIds != null ? await TeamUpdateManager.getGroup(groupId) : null;

    const payload: Record<string, unknown> = { ...updates };
    if (updates.memberIds) {
      payload.memberIds = Array.from(new Set(updates.memberIds.filter(Boolean)));
    }
    if (typeof updates.name === 'string') {
      payload.name = updates.name.trim();
    }
    if (typeof updates.description === 'string') {
      payload.description = updates.description.trim();
    }
    if (typeof updates.atAGlance === 'string') {
      payload.atAGlance = updates.atAGlance.trim();
    }
    await BaseCRUDManager.update(GROUPS, groupId, payload);

    if (previous && updates.memberIds) {
      const previousIds = new Set(previous.memberIds || []);
      const added = updates.memberIds.filter((id) => id && !previousIds.has(id));
      if (added.length) {
        const refreshed = await TeamUpdateManager.getGroup(groupId);
        if (refreshed) {
          void notifyTeamUpdateMembersAdded({
            group: refreshed,
            memberIds: added,
          }).catch((error) => console.error('Team update added emails failed', error));
        }
      }
    }
  }

  static async deleteGroup(groupId: string): Promise<void> {
    const [posts, replies, votes, reactions, requestResponses, activities, signals] =
      await Promise.all([
        BaseCRUDManager.query<ITeamUpdatePost>(POSTS, {
          filters: [{ field: 'groupId', operator: '==', value: groupId }],
        }),
        BaseCRUDManager.query<ITeamUpdateReply>(REPLIES, {
          filters: [{ field: 'groupId', operator: '==', value: groupId }],
        }),
        BaseCRUDManager.query<ITeamUpdatePollVote>(POLL_VOTES, {
          filters: [{ field: 'groupId', operator: '==', value: groupId }],
        }),
        BaseCRUDManager.query<ITeamUpdateReaction>(REACTIONS, {
          filters: [{ field: 'groupId', operator: '==', value: groupId }],
        }),
        BaseCRUDManager.query<ITeamUpdateRequestResponse>(REQUEST_RESPONSES, {
          filters: [{ field: 'groupId', operator: '==', value: groupId }],
        }),
        BaseCRUDManager.query<{ id: string }>(MEMBER_ACTIVITY, {
          filters: [{ field: 'groupId', operator: '==', value: groupId }],
        }),
        BaseCRUDManager.query<{ id: string }>(SIGNALLING, {
          filters: [{ field: 'baseSessionId', operator: '==', value: groupId }],
        }),
      ]);

    await Promise.all(votes.map((vote) => BaseCRUDManager.delete(POLL_VOTES, vote.id)));
    await Promise.all(
      reactions.map((reaction) => BaseCRUDManager.delete(REACTIONS, reaction.id))
    );
    await Promise.all(
      requestResponses.map((response) =>
        BaseCRUDManager.delete(REQUEST_RESPONSES, response.id)
      )
    );
    await Promise.all(
      activities.map((activity) => BaseCRUDManager.delete(MEMBER_ACTIVITY, activity.id))
    );
    await Promise.all(
      signals.map((signal) =>
        BaseCRUDManager.delete(SIGNALLING, signal.id).catch(() => undefined)
      )
    );
    await Promise.all(
      replies.map(async (reply) => {
        await deleteAttachmentFiles(reply.attachments);
        await deleteStoragePrefix(groupId, reply.id);
        await BaseCRUDManager.delete(REPLIES, reply.id);
      })
    );
    await Promise.all(
      posts.map(async (post) => {
        await deleteAttachmentFiles(buildAttachmentsFromFlatFields(post));
        await deleteStoragePrefix(groupId, post.id);
        await BaseCRUDManager.delete(POSTS, post.id);
      })
    );
    await BaseCRUDManager.delete(CALLS, groupId).catch(() => undefined);
    await deleteStorageFolder(`team-updates/${groupId}`);
    await BaseCRUDManager.delete(GROUPS, groupId);
  }

  static async getGroup(groupId: string): Promise<ITeamUpdateGroup | null> {
    return BaseCRUDManager.getById<ITeamUpdateGroup>(GROUPS, groupId);
  }

  static async listAllGroups(): Promise<ITeamUpdateGroup[]> {
    return BaseCRUDManager.query<ITeamUpdateGroup>(GROUPS, {
      orderBy: [{ field: 'updatedAt', direction: 'desc' }],
    });
  }

  static async listGroupsForUser(userId: string): Promise<ITeamUpdateGroup[]> {
    return BaseCRUDManager.query<ITeamUpdateGroup>(GROUPS, {
      filters: [{ field: 'memberIds', operator: 'array-contains', value: userId }],
      orderBy: [{ field: 'updatedAt', direction: 'desc' }],
    });
  }

  static async listEligibleMembers(
    organisationId: string,
    roles: MemberRole[] = TEAM_UPDATE_MEMBER_ROLES
  ): Promise<IMember[]> {
    const members = await getOrganisationMembers(organisationId);
    const orgEligible = members.filter(
      (member) => !member.isTemporary && hasAnyRole(member.roles, roles)
    );

    const platformAdmins = await UserManager.getPlatformAdminUsers();
    const byId = new Map<string, IMember>();
    orgEligible.forEach((member) => byId.set(member.userKey, member));

    platformAdmins.forEach((admin) => {
      if (byId.has(admin.userKey)) return;
      byId.set(admin.userKey, {
        userKey: admin.userKey,
        organisationId,
        roles: [admin.role === 'superAdmin' ? 'superAdmin' : 'admin'],
        name: admin.userName || admin.email || 'Platform admin',
        email: admin.email,
        joinedAt: Timestamp.now(),
        groupIds: [],
      });
    });

    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  static observeGroupPosts(
    groupId: string,
    callback: (posts: ITeamUpdatePost[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, POSTS),
      where('groupId', '==', groupId),
      orderBy('createdAt', 'asc')
    );
    return onSnapshot(
      q,
      (snapshot) =>
        callback(
          mapDocs<ITeamUpdatePost>(snapshot).map((post) =>
            normaliseTeamUpdatePostMedia(post)
          )
        ),
      (error) => {
        console.error('Error observing team update posts:', error);
        callback([]);
      }
    );
  }

  static observeGroupReplies(
    groupId: string,
    callback: (replies: ITeamUpdateReply[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, REPLIES),
      where('groupId', '==', groupId),
      orderBy('createdAt', 'asc')
    );
    return onSnapshot(
      q,
      (snapshot) =>
        callback(
          mapDocs<ITeamUpdateReply>(snapshot).map((reply) =>
            normaliseTeamUpdateReplyMedia(reply)
          )
        ),
      (error) => {
        console.error('Error observing team update replies:', error);
        callback([]);
      }
    );
  }

  static observeGroupPollVotes(
    groupId: string,
    callback: (votes: ITeamUpdatePollVote[]) => void
  ): Unsubscribe {
    const q = query(collection(db, POLL_VOTES), where('groupId', '==', groupId));
    return onSnapshot(
      q,
      (snapshot) => callback(mapDocs<ITeamUpdatePollVote>(snapshot)),
      (error) => {
        console.error('Error observing team update poll votes:', error);
        callback([]);
      }
    );
  }

  static observeGroupRequestResponses(
    groupId: string,
    callback: (responses: ITeamUpdateRequestResponse[]) => void
  ): Unsubscribe {
    const q = query(collection(db, REQUEST_RESPONSES), where('groupId', '==', groupId));
    return onSnapshot(
      q,
      (snapshot) => callback(mapDocs<ITeamUpdateRequestResponse>(snapshot)),
      (error) => {
        console.error('Error observing team update request responses:', error);
        callback([]);
      }
    );
  }

  static observeGroupReactions(
    groupId: string,
    callback: (reactions: ITeamUpdateReaction[]) => void
  ): Unsubscribe {
    const q = query(collection(db, REACTIONS), where('groupId', '==', groupId));
    return onSnapshot(
      q,
      (snapshot) => callback(mapDocs<ITeamUpdateReaction>(snapshot)),
      (error) => {
        console.error('Error observing team update reactions:', error);
        callback([]);
      }
    );
  }

  /** Live member delivery/view watermarks for WhatsApp-style ticks. */
  static observeGroupMemberActivity(
    groupId: string,
    callback: (activities: TeamUpdateMemberActivity[]) => void
  ): Unsubscribe {
    const q = query(collection(db, MEMBER_ACTIVITY), where('groupId', '==', groupId));
    return onSnapshot(
      q,
      (snapshot) => callback(mapDocs<TeamUpdateMemberActivity>(snapshot)),
      (error) => {
        console.error('Error observing team update member activity:', error);
        callback([]);
      }
    );
  }

  static async createPost(input: CreateTeamUpdatePostInput): Promise<string> {
    const title = input.title.trim();
    const body = input.body.trim();
    const pollOptions = normalisePollOptions(input.poll?.options).slice(
      0,
      TEAM_UPDATE_MAX_POLL_OPTIONS
    );
    const customId = input.customId || this.allocateMessageId('post');
    const payload: Record<string, unknown> = {
      groupId: input.groupId,
      type: input.type,
      title,
      body,
      authorId: input.authorId,
      authorName: input.authorName,
      replyCount: 0,
    };
    if (pollOptions.length >= TEAM_UPDATE_MIN_POLL_OPTIONS) {
      payload.poll = {
        options: pollOptions,
        allowMultiple: Boolean(input.poll?.allowMultiple),
      };
    }
    if (input.type === 'request') {
      const assignees = normaliseRequestAssignees(input.requestAssignees);
      if (assignees.length === 0) {
        throw new Error('Request posts require at least one assignee');
      }
      payload.requestAssignees = assignees;
      // Dual-write legacy single fields for older readers.
      payload.requestAssigneeId = assignees[0].userId;
      payload.requestAssigneeName = assignees[0].name;
      payload.requestStatus = 'pending';
    }
    if (input.workStreamId?.trim()) payload.workStreamId = input.workStreamId.trim();
    if (input.workStreamTitle?.trim()) {
      payload.workStreamTitle = input.workStreamTitle.trim().slice(0, 200);
    }
    if (input.contributionTaskId?.trim()) {
      payload.contributionTaskId = input.contributionTaskId.trim();
    }
    if (input.attachments?.length) {
      payload.attachments = input.attachments;
      Object.assign(payload, flatMediaFieldsFromAttachments(input.attachments));
    }
    const id = await BaseCRUDManager.create(
      POSTS,
      payload as Omit<ITeamUpdatePost, 'id' | 'createdAt' | 'updatedAt'>,
      { customId }
    );
    await this.touchGroupActivity(input.groupId);

    const voice = input.attachments?.find(
      (attachment): attachment is TeamUpdateVoiceAttachment => attachment.type === 'voice'
    );
    if (voice) {
      requestVoiceTranscription({
        kind: 'post',
        messageId: id,
        storagePath: voice.storagePath,
      });
    }
    return id;
  }

  static async updatePost(postId: string, updates: UpdateTeamUpdatePostInput): Promise<void> {
    const payload: Record<string, unknown> = {
      editedAt: Timestamp.now(),
    };
    if (typeof updates.title === 'string') payload.title = updates.title.trim();
    if (typeof updates.body === 'string') payload.body = updates.body.trim();
    if (updates.type) payload.type = updates.type;
    if (updates.requestAssignees) {
      const assignees = normaliseRequestAssignees(updates.requestAssignees);
      payload.requestAssignees = assignees;
      if (assignees[0]) {
        payload.requestAssigneeId = assignees[0].userId;
        payload.requestAssigneeName = assignees[0].name;
      }
    }
    if (updates.poll === null) {
      payload.poll = deleteField();
    } else if (updates.poll) {
      const pollOptions = normalisePollOptions(updates.poll.options).slice(
        0,
        TEAM_UPDATE_MAX_POLL_OPTIONS
      );
      if (pollOptions.length >= TEAM_UPDATE_MIN_POLL_OPTIONS) {
        payload.poll = {
          options: pollOptions,
          allowMultiple: Boolean(updates.poll.allowMultiple),
        };
      }
    }
    if (updates.attachments) {
      payload.attachments = updates.attachments;
      Object.assign(payload, flatMediaFieldsFromAttachments(updates.attachments));
    }
    await BaseCRUDManager.update(POSTS, postId, payload);

    const voice = updates.attachments?.find(
      (attachment): attachment is TeamUpdateVoiceAttachment =>
        attachment.type === 'voice' && attachment.transcriptStatus === 'pending'
    );
    if (voice) {
      requestVoiceTranscription({
        kind: 'post',
        messageId: postId,
        storagePath: voice.storagePath,
      });
    }
  }

  static async deletePost(postId: string): Promise<void> {
    const post = await BaseCRUDManager.getById<ITeamUpdatePost>(POSTS, postId);
    if (!post) return;

    const [replies, votes, reactions, requestResponses] = await Promise.all([
      BaseCRUDManager.query<ITeamUpdateReply>(REPLIES, {
        filters: [{ field: 'postId', operator: '==', value: postId }],
      }),
      BaseCRUDManager.query<ITeamUpdatePollVote>(POLL_VOTES, {
        filters: [{ field: 'postId', operator: '==', value: postId }],
      }),
      BaseCRUDManager.query<ITeamUpdateReaction>(REACTIONS, {
        filters: [{ field: 'postId', operator: '==', value: postId }],
      }),
      BaseCRUDManager.query<ITeamUpdateRequestResponse>(REQUEST_RESPONSES, {
        filters: [{ field: 'postId', operator: '==', value: postId }],
      }),
    ]);

    await Promise.all(votes.map((vote) => BaseCRUDManager.delete(POLL_VOTES, vote.id)));
    await Promise.all(
      reactions.map((reaction) => BaseCRUDManager.delete(REACTIONS, reaction.id))
    );
    await Promise.all(
      requestResponses.map((response) =>
        BaseCRUDManager.delete(REQUEST_RESPONSES, response.id)
      )
    );
    await Promise.all(
      replies.map(async (reply) => {
        await deleteAttachmentFiles(reply.attachments);
        await deleteStoragePrefix(post.groupId, reply.id);
        await BaseCRUDManager.delete(REPLIES, reply.id);
      })
    );
    await deleteAttachmentFiles(buildAttachmentsFromFlatFields(post));
    await deleteStoragePrefix(post.groupId, post.id);
    await BaseCRUDManager.delete(POSTS, postId);
  }

  /**
   * Toggle a reaction on a post. Same emoji again removes it; users may hold
   * multiple different emojis on one post.
   */
  static async toggleReaction(input: {
    groupId: string;
    postId: string;
    emoji: TeamUpdateReactionId;
    authorId: string;
  }): Promise<void> {
    if (!(TEAM_UPDATE_REACTION_IDS as readonly string[]).includes(input.emoji)) {
      throw new Error(`Unsupported reaction: ${input.emoji}`);
    }
    const reactionId = `${input.postId}_${input.authorId}_${input.emoji}`;
    const refDoc = doc(db, REACTIONS, reactionId);
    const existing = await getDoc(refDoc);
    if (existing.exists()) {
      await BaseCRUDManager.delete(REACTIONS, reactionId);
      return;
    }
    await setDoc(refDoc, {
      groupId: input.groupId,
      postId: input.postId,
      authorId: input.authorId,
      emoji: input.emoji,
      createdAt: Timestamp.now(),
    });
  }

  static async castPollVote(input: {
    groupId: string;
    postId: string;
    optionId: string;
    authorId: string;
  }): Promise<void> {
    const voteId = `${input.postId}_${input.authorId}`;
    const refDoc = doc(db, POLL_VOTES, voteId);
    const existing = await getDoc(refDoc);
    const now = Timestamp.now();
    if (existing.exists()) {
      await updateDoc(refDoc, {
        optionId: input.optionId,
        updatedAt: now,
      });
      return;
    }
    await setDoc(refDoc, {
      groupId: input.groupId,
      postId: input.postId,
      optionId: input.optionId,
      authorId: input.authorId,
      createdAt: now,
      updatedAt: now,
    });
  }

  /**
   * An assignee accepts a request. Creates a personal to-do once (idempotent).
   * Response is stored per user in `teamUpdateRequestResponses`.
   */
  static async acceptRequest(input: {
    postId: string;
    userId: string;
    organisationId: string;
  }): Promise<void> {
    const post = await BaseCRUDManager.getById<ITeamUpdatePost>(POSTS, input.postId);
    if (!post || post.type !== 'request') {
      throw new Error('Request post not found');
    }
    const assignees = getTeamUpdateRequestAssignees(post);
    if (!assignees.some((assignee) => assignee.userId === input.userId)) {
      throw new Error('Only an assignee can accept this request');
    }

    const responseId = `${post.id}_${input.userId}`;
    const responseRef = doc(db, REQUEST_RESPONSES, responseId);
    const existingResponse = await getDoc(responseRef);
    const existingStatus = existingResponse.exists()
      ? (existingResponse.data()?.status as ITeamUpdateRequestResponse['status'] | undefined)
      : getTeamUpdateRequestStatusForUser(post, input.userId, []);
    if (existingStatus === 'accepted') return;
    if (existingStatus === 'declined') {
      throw new Error('This request was already declined');
    }

    const title =
      (post.title || '').trim() ||
      (post.body || '').trim().split('\n')[0]?.slice(0, 120) ||
      'Team update request';

    const existingTodos = await WorkStreamTodoManager.listTodosForUser(
      input.organisationId,
      input.userId
    );
    const alreadyMirrored = existingTodos.find(
      (todo) => todo.teamUpdatePostId === post.id && !todo.completed
    );
    let todoId =
      alreadyMirrored?.id ||
      (existingResponse.exists()
        ? (existingResponse.data()?.todoId as string | undefined)
        : undefined);
    if (!todoId) {
      todoId = await WorkStreamTodoManager.createTodo({
        organisationId: input.organisationId,
        userKey: input.userId,
        title,
        source: 'team_update_request',
        teamUpdatePostId: post.id,
        teamUpdateGroupId: post.groupId,
        workStreamId: post.workStreamId,
        contributionTaskId: post.contributionTaskId,
      });
      if (post.workStreamId && post.contributionTaskId) {
        await WorkStreamManager.claimContributionTask({
          workStreamId: post.workStreamId,
          taskId: post.contributionTaskId,
          userKey: input.userId,
        });
      }
    }

    const now = Timestamp.now();
    await setDoc(
      responseRef,
      {
        groupId: post.groupId,
        postId: post.id,
        authorId: input.userId,
        status: 'accepted',
        todoId,
        createdAt: existingResponse.exists()
          ? existingResponse.data()?.createdAt || now
          : now,
        updatedAt: now,
      },
      { merge: true }
    );

    // Keep legacy single-assignee fields in sync when this is a one-person ask.
    if (assignees.length === 1 && assignees[0].userId === input.userId) {
      await updateDoc(doc(db, POSTS, post.id), {
        requestStatus: 'accepted',
        requestRespondedAt: now,
        requestTodoId: todoId,
        updatedAt: now,
      });
    }
  }

  /** An assignee declines a request. */
  static async declineRequest(input: {
    postId: string;
    userId: string;
  }): Promise<void> {
    const post = await BaseCRUDManager.getById<ITeamUpdatePost>(POSTS, input.postId);
    if (!post || post.type !== 'request') {
      throw new Error('Request post not found');
    }
    const assignees = getTeamUpdateRequestAssignees(post);
    if (!assignees.some((assignee) => assignee.userId === input.userId)) {
      throw new Error('Only an assignee can decline this request');
    }

    const responseId = `${post.id}_${input.userId}`;
    const responseRef = doc(db, REQUEST_RESPONSES, responseId);
    const existingResponse = await getDoc(responseRef);
    const existingStatus = existingResponse.exists()
      ? (existingResponse.data()?.status as ITeamUpdateRequestResponse['status'] | undefined)
      : getTeamUpdateRequestStatusForUser(post, input.userId, []);
    if (existingStatus === 'declined') return;
    if (existingStatus === 'accepted') {
      throw new Error('This request was already accepted');
    }

    const now = Timestamp.now();
    await setDoc(
      responseRef,
      {
        groupId: post.groupId,
        postId: post.id,
        authorId: input.userId,
        status: 'declined',
        createdAt: existingResponse.exists()
          ? existingResponse.data()?.createdAt || now
          : now,
        updatedAt: now,
      },
      { merge: true }
    );

    if (assignees.length === 1 && assignees[0].userId === input.userId) {
      await updateDoc(doc(db, POSTS, post.id), {
        requestStatus: 'declined',
        requestRespondedAt: now,
        updatedAt: now,
      });
    }
  }

  static async createReply(input: CreateTeamUpdateReplyInput): Promise<string> {
    const content = input.content.trim();
    const customId = input.customId || this.allocateMessageId('reply');
    const payload: Record<string, unknown> = {
      groupId: input.groupId,
      postId: input.postId,
      authorId: input.authorId,
      authorName: input.authorName,
      content,
    };
    if (input.attachments?.length) {
      payload.attachments = input.attachments;
      Object.assign(payload, flatMediaFieldsFromAttachments(input.attachments));
    }
    const replyId = await BaseCRUDManager.create(
      REPLIES,
      payload as Omit<ITeamUpdateReply, 'id' | 'createdAt' | 'updatedAt'>,
      { customId }
    );
    await this.updatePostReplyStats(input.postId);
    await this.touchGroupActivity(input.groupId);

    const voice = input.attachments?.find(
      (attachment): attachment is TeamUpdateVoiceAttachment => attachment.type === 'voice'
    );
    if (voice) {
      requestVoiceTranscription({
        kind: 'reply',
        messageId: replyId,
        storagePath: voice.storagePath,
      });
    }
    return replyId;
  }

  static async updateReply(replyId: string, updates: UpdateTeamUpdateReplyInput): Promise<void> {
    const payload: Record<string, unknown> = {
      editedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    if (typeof updates.content === 'string') payload.content = updates.content.trim();
    if (updates.attachments) {
      payload.attachments = updates.attachments;
      Object.assign(payload, flatMediaFieldsFromAttachments(updates.attachments));
    }
    await BaseCRUDManager.update(REPLIES, replyId, payload);

    const voice = updates.attachments?.find(
      (attachment): attachment is TeamUpdateVoiceAttachment =>
        attachment.type === 'voice' && attachment.transcriptStatus === 'pending'
    );
    if (voice) {
      requestVoiceTranscription({
        kind: 'reply',
        messageId: replyId,
        storagePath: voice.storagePath,
      });
    }
  }

  static async deleteReply(replyId: string): Promise<void> {
    const reply = await BaseCRUDManager.getById<ITeamUpdateReply>(REPLIES, replyId);
    if (!reply) return;
    await deleteAttachmentFiles(buildAttachmentsFromFlatFields(reply));
    await deleteStoragePrefix(reply.groupId, reply.id);
    await BaseCRUDManager.delete(REPLIES, replyId);
    await this.updatePostReplyStats(reply.postId);
  }

  private static async updatePostReplyStats(postId: string): Promise<void> {
    try {
      const replies = await BaseCRUDManager.query<ITeamUpdateReply>(REPLIES, {
        filters: [{ field: 'postId', operator: '==', value: postId }],
      });
      const replyCount = replies.length;
      let lastReplyAt: Timestamp | undefined;
      if (replyCount > 0) {
        const sorted = [...replies].sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });
        lastReplyAt = sorted[0].createdAt;
      }
      const updateData: Partial<ITeamUpdatePost> = { replyCount };
      if (lastReplyAt) {
        updateData.lastReplyAt = lastReplyAt;
      }
      await BaseCRUDManager.update(POSTS, postId, updateData);
    } catch (error) {
      console.error('Error updating team update reply stats:', error);
    }
  }

  static canAccessGroup(
    group: ITeamUpdateGroup | null | undefined,
    userId: string | undefined,
    isSuperAdmin: boolean
  ): boolean {
    if (!group || !userId) return false;
    if (isSuperAdmin) return true;
    return group.memberIds.includes(userId);
  }

  /** Bump group `updatedAt` so chats reorder and unread checks can use activity time. */
  static async touchGroupActivity(groupId: string): Promise<void> {
    if (!groupId) return;
    try {
      await updateDoc(doc(db, GROUPS, groupId), { updatedAt: Timestamp.now() });
    } catch (error) {
      console.error('Failed to touch team update group activity', { groupId, error });
    }
  }

  /**
   * Remove the current user from a chat. Allowed by Firestore for self-leave only.
   */
  static async leaveGroup(groupId: string, userId: string): Promise<void> {
    if (!groupId || !userId) {
      throw new Error('groupId and userId are required to leave a group');
    }
    const group = await TeamUpdateManager.getGroup(groupId);
    if (!group) {
      throw new Error('Group not found');
    }
    if (!group.memberIds.includes(userId)) {
      return;
    }
    await updateDoc(doc(db, GROUPS, groupId), {
      memberIds: arrayRemove(userId),
      updatedAt: Timestamp.now(),
    });
  }

  /** Resolve display names for group members (authenticated Users read). */
  static async listGroupMembers(
    memberIds: string[],
    organisationId?: string
  ): Promise<TeamUpdateGroupMember[]> {
    const ids = Array.from(new Set(memberIds.filter(Boolean)));
    if (!ids.length) return [];

    const users = await UserManager.getUsersByKeys(ids);
    const byId = new Map(users.map((user) => [user.userKey || '', user]));

    return ids
      .map((userId) => {
        const user = byId.get(userId);
        const orgName =
          organisationId && user?.Organisations?.[organisationId]?.name
            ? user.Organisations[organisationId].name
            : undefined;
        const name =
          user?.userProfile?.userName ||
          orgName ||
          (typeof user?.email === 'string' && user.email.includes('@') ? user.email : '') ||
          userId;
        return {
          userId,
          name,
          email: typeof user?.email === 'string' ? user.email : undefined,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Per-group unread counts (posts + replies by others since last viewed).
   * Used for Org Menu badges and chat list indicators.
   */
  static async getUnreadCountsByGroup(
    userId: string
  ): Promise<{ byGroupId: Record<string, number>; total: number }> {
    if (!userId) return { byGroupId: {}, total: 0 };

    const groups = await TeamUpdateManager.listGroupsForUser(userId);
    if (!groups.length) return { byGroupId: {}, total: 0 };

    const results = await Promise.all(
      groups.map(async (group) => {
        const [activity, posts, replies] = await Promise.all([
          getTeamUpdateMemberActivity(group.id, userId).catch(
            () => null as TeamUpdateMemberActivity | null
          ),
          BaseCRUDManager.query<ITeamUpdatePost>(POSTS, {
            filters: [{ field: 'groupId', operator: '==', value: group.id }],
            orderBy: [{ field: 'createdAt', direction: 'asc' }],
          }).catch(() => [] as ITeamUpdatePost[]),
          BaseCRUDManager.query<ITeamUpdateReply>(REPLIES, {
            filters: [{ field: 'groupId', operator: '==', value: group.id }],
            orderBy: [{ field: 'createdAt', direction: 'asc' }],
          }).catch(() => [] as ITeamUpdateReply[]),
        ]);

        const count = totalUnreadFromPostsAndReplies(
          posts,
          replies,
          userId,
          activity?.lastViewedAt
        );
        return { groupId: group.id, count };
      })
    );

    const byGroupId: Record<string, number> = {};
    let total = 0;
    results.forEach(({ groupId, count }) => {
      if (count > 0) {
        byGroupId[groupId] = count;
        total += count;
      }
    });
    return { byGroupId, total };
  }

  static async getTotalUnreadCount(userId: string): Promise<number> {
    const { total } = await TeamUpdateManager.getUnreadCountsByGroup(userId);
    return total;
  }
}
